/**
 * The learner reducer.
 *
 * Every change to a student's state goes through these pure functions, so the
 * rules live in one tested place and the React layer only stores the result.
 * `now` is always injected — no clock reads here.
 */

import type { GradeResult } from '@yuan-math/exercises';
import { computeMastery, createConceptState, RECENT_WINDOW, type ConceptState, type MasteryBreakdown } from './mastery.ts';
import { qualityFromAttempt, scheduleNext } from './review.ts';
import {
  levelForXp,
  newlyEarned,
  updateStreak,
  xpForAttempt,
  XP_LESSON_COMPLETE,
  type Achievement,
} from './progress.ts';
import { rollDaily, settleQuests, type Quest } from './quests.ts';
import { ATTEMPT_LOG_LIMIT, type AttemptRecord, type DailyProgress, type LearnerProfile } from './types.ts';

export type AttemptInput = {
  conceptId: string;
  exerciseId: string;
  difficulty: number;
  result: GradeResult;
  /** Milliseconds from the question appearing to the answer being submitted. */
  responseMs?: number;
  usedHint?: boolean;
  /** False when the student is re-submitting after a wrong answer. */
  firstTry: boolean;
  /** The attempt came from the review queue. */
  isReview?: boolean;
  now: number;
};

export type AttemptOutcome = {
  profile: LearnerProfile;
  /** XP for the answer itself, combo multiplier included. */
  xpGained: number;
  /** XP paid for any quest this answer finished. Zero on most answers. */
  bonusXp: number;
  masteryBefore: MasteryBreakdown;
  masteryAfter: MasteryBreakdown;
  unlockedAchievements: Achievement[];
  /** True when this attempt pushed the concept over the mastery line. */
  justMastered: boolean;
  /** Consecutive correct answers after this one. Zero after a wrong answer. */
  combo: number;
  /** Quests finished by this answer. */
  completedQuests: Quest[];
  /** True when that was the last outstanding quest of the day. */
  sweptTheDay: boolean;
  /** Set when the XP for this answer crossed a level boundary. */
  leveledUpTo?: number;
};

/**
 * Records one graded attempt.
 *
 * Malformed input — an empty box, "half", `3/0` — is not an attempt. It does
 * not touch mastery, streaks or XP, because punishing a typo teaches students
 * to fear the answer box.
 */
export function applyAttempt(profile: LearnerProfile, input: AttemptInput): AttemptOutcome {
  const { conceptId, now, result } = input;
  const before = computeMastery(profile.concepts[conceptId], now);

  if (result.status === 'malformed') {
    return {
      profile,
      xpGained: 0,
      bonusXp: 0,
      masteryBefore: before,
      masteryAfter: before,
      unlockedAchievements: [],
      justMastered: false,
      // The combo survives a typo. Breaking a run of nine because a phone
      // keyboard produced an empty box would be the same mistake as counting
      // it as a wrong answer.
      combo: rollDaily(profile, now).combo,
      completedQuests: [],
      sweptTheDay: false,
    };
  }

  const previous = profile.concepts[conceptId] ?? createConceptState(conceptId);
  const correct = result.correct;
  const isHard = input.difficulty >= 4;

  // Today's counters, which the combo and the day's quests both read from.
  const dailyBefore = rollDaily(profile, now);
  const comboBefore = dailyBefore.combo;

  const state: ConceptState = {
    ...previous,
    // Practice supersedes the placement estimate: from here the concept has a
    // real history and is read like any other.
    placed: false,
    attempts: previous.attempts + 1,
    correct: previous.correct + (correct ? 1 : 0),
    recent: [...previous.recent, correct].slice(-RECENT_WINDOW),
    hardAttempts: previous.hardAttempts + (isHard ? 1 : 0),
    hardCorrect: previous.hardCorrect + (isHard && correct ? 1 : 0),
    ceiling: correct && input.firstTry ? Math.max(previous.ceiling, input.difficulty) : previous.ceiling,
    firstStudiedAt: previous.firstStudiedAt ?? now,
    lastPracticedAt: now,
    misconceptions: result.misconceptionId
      ? { ...previous.misconceptions, [result.misconceptionId]: (previous.misconceptions[result.misconceptionId] ?? 0) + 1 }
      : previous.misconceptions,
  };

  const quality = qualityFromAttempt({
    correct,
    ...(input.responseMs !== undefined ? { responseMs: input.responseMs } : {}),
    ...(input.usedHint !== undefined ? { usedHint: input.usedHint } : {}),
    attemptsOnItem: input.firstTry ? 1 : 2,
    difficulty: input.difficulty,
  });

  // The review ladder advances once per *session*, not once per problem.
  // Twelve correct answers in one sitting is one successful recall of the
  // concept; treating it as twelve would push the next review out by months
  // and defeat the point of spacing. A failure always reschedules, because a
  // concept the student just got wrong needs to come back tomorrow.
  const dueNow = previous.dueAt === undefined || previous.dueAt <= now;
  const scheduled: ConceptState =
    !correct || dueNow ? { ...state, ...scheduleNext(previous, quality, now) } : state;

  const record: AttemptRecord = {
    at: now,
    conceptId,
    exerciseId: input.exerciseId,
    correct,
    status: result.status,
    difficulty: input.difficulty,
    ...(input.responseMs !== undefined ? { responseMs: input.responseMs } : {}),
    ...(input.usedHint ? { usedHint: true } : {}),
    ...(input.isReview ? { isReview: true } : {}),
    ...(result.misconceptionId ? { misconceptionId: result.misconceptionId } : {}),
  };

  // The combo multiplier is applied at the run *before* this answer, so the
  // first correct answer is worth its plain value and the bonus builds from
  // there. A wrong answer costs the run, never any XP already earned.
  const xpGained = xpForAttempt({
    correct,
    difficulty: input.difficulty,
    firstTry: input.firstTry,
    combo: comboBefore,
    ...(input.isReview !== undefined ? { isReview: input.isReview } : {}),
    ...(input.usedHint !== undefined ? { usedHint: input.usedHint } : {}),
  });

  const combo = correct ? comboBefore + 1 : 0;
  const counted: DailyProgress = {
    ...dailyBefore,
    attempts: dailyBefore.attempts + 1,
    correct: dailyBefore.correct + (correct ? 1 : 0),
    hardCorrect: dailyBefore.hardCorrect + (correct && isHard ? 1 : 0),
    unaidedCorrect: dailyBefore.unaidedCorrect + (correct && input.firstTry && !input.usedHint ? 1 : 0),
    reviewAttempts: dailyBefore.reviewAttempts + (input.isReview ? 1 : 0),
    combo,
    bestCombo: Math.max(dailyBefore.bestCombo, combo),
    conceptIds: dailyBefore.conceptIds.includes(conceptId)
      ? dailyBefore.conceptIds
      : [...dailyBefore.conceptIds, conceptId],
  };

  const settled = settleQuests(counted);
  const levelBefore = levelForXp(profile.xp).level;

  let next: LearnerProfile = {
    ...profile,
    xp: profile.xp + xpGained + settled.xp,
    streak: updateStreak(profile.streak, now),
    concepts: { ...profile.concepts, [conceptId]: scheduled },
    attempts: [...profile.attempts, record].slice(-ATTEMPT_LOG_LIMIT),
    daily: settled.daily,
    stats: {
      ...profile.stats,
      totalAttempts: profile.stats.totalAttempts + 1,
      totalCorrect: profile.stats.totalCorrect + (correct ? 1 : 0),
      totalStudyMs: profile.stats.totalStudyMs + (input.responseMs ?? 0),
      bestCombo: Math.max(profile.stats.bestCombo ?? 0, combo),
      questsCompleted: (profile.stats.questsCompleted ?? 0) + settled.completed.length,
    },
  };

  const unlocked = newlyEarned(next, now);
  if (unlocked.length > 0) {
    next = {
      ...next,
      achievements: [...next.achievements, ...unlocked.map((item) => ({ id: item.id, unlockedAt: now }))],
    };
  }

  const after = computeMastery(scheduled, now);
  const levelAfter = levelForXp(next.xp).level;

  return {
    profile: next,
    xpGained,
    bonusXp: settled.xp,
    masteryBefore: before,
    masteryAfter: after,
    unlockedAchievements: unlocked,
    justMastered: before.level !== 'mastered' && after.level === 'mastered',
    combo,
    completedQuests: settled.completed,
    sweptTheDay: settled.sweptTheDay,
    ...(levelAfter > levelBefore ? { leveledUpTo: levelAfter } : {}),
  };
}

export type LessonCompletion = {
  profile: LearnerProfile;
  xpGained: number;
  /** XP paid for any quest finishing the lesson completed. */
  bonusXp: number;
  unlockedAchievements: Achievement[];
  completedQuests: Quest[];
  sweptTheDay: boolean;
};

/** Marks a lesson finished. Idempotent — re-reading a lesson earns nothing. */
export function completeLesson(
  profile: LearnerProfile,
  input: { lessonId: string; conceptId: string; now: number },
): LessonCompletion {
  if (profile.completedLessonIds.includes(input.lessonId)) {
    return { profile, xpGained: 0, bonusXp: 0, unlockedAchievements: [], completedQuests: [], sweptTheDay: false };
  }

  const previous = profile.concepts[input.conceptId] ?? createConceptState(input.conceptId);
  const state: ConceptState = {
    ...previous,
    lessonCompleted: true,
    firstStudiedAt: previous.firstStudiedAt ?? input.now,
  };

  const dailyBefore = rollDaily(profile, input.now);
  const settled = settleQuests({ ...dailyBefore, lessonsCompleted: dailyBefore.lessonsCompleted + 1 });

  let next: LearnerProfile = {
    ...profile,
    xp: profile.xp + XP_LESSON_COMPLETE + settled.xp,
    streak: updateStreak(profile.streak, input.now),
    completedLessonIds: [...profile.completedLessonIds, input.lessonId],
    concepts: { ...profile.concepts, [input.conceptId]: state },
    daily: settled.daily,
    stats: {
      ...profile.stats,
      lessonsCompleted: profile.stats.lessonsCompleted + 1,
      questsCompleted: (profile.stats.questsCompleted ?? 0) + settled.completed.length,
    },
  };

  const unlocked = newlyEarned(next, input.now);
  if (unlocked.length > 0) {
    next = {
      ...next,
      achievements: [...next.achievements, ...unlocked.map((item) => ({ id: item.id, unlockedAt: input.now }))],
    };
  }

  return {
    profile: next,
    xpGained: XP_LESSON_COMPLETE,
    bonusXp: settled.xp,
    unlockedAchievements: unlocked,
    completedQuests: settled.completed,
    sweptTheDay: settled.sweptTheDay,
  };
}

/**
 * Applies a diagnostic result as prior evidence, not as free mastery.
 *
 * The two kinds of evidence a placement test produces are written differently,
 * because they are not the same thing. A concept the student was *asked* about
 * and answered correctly earns a real attempt and a real review date — one
 * graded answer happened, and recording it is honest. A concept merely
 * *implied* by that answer earns the `placed` mark and nothing else.
 *
 * Writing a fabricated attempt for the implied ones was the bug behind "I was
 * placed at Grade 12 and my home page wants me to practise counting". Fifty-odd
 * concepts each got a single invented correct answer, which computes to a
 * mastery of about 0.54 — below the repair ceiling and far below mastery. So
 * every one of them looked like a concept the student had started and was
 * struggling with, and the recommender did exactly what it should do with such
 * a concept: offered to take them back to it.
 */
export function applyDiagnostic(
  profile: LearnerProfile,
  input: {
    assumedMastered: string[];
    /** The subset that was actually asked. Defaults to none — all implied. */
    demonstrated?: string[];
    summary: LearnerProfile['diagnostic'];
    now: number;
  },
): LearnerProfile {
  const concepts = { ...profile.concepts };
  const answered = new Set(input.demonstrated ?? []);

  for (const conceptId of input.assumedMastered) {
    const previous = concepts[conceptId] ?? createConceptState(conceptId);

    // A retake never relabels work the student has actually done: an estimate
    // is only worth writing where there is no evidence to estimate from.
    const placed = previous.attempts === 0;

    if (!answered.has(conceptId)) {
      // Nobody asked. Record the inference and no evidence whatsoever, so the
      // concept reads as "not started" everywhere mastery is reported — which
      // is the truth — while the recommender can still see it is not a gap.
      // The first real attempt clears the mark and takes over from it.
      concepts[conceptId] = { ...previous, ...(placed ? { placed: true } : {}) };
      continue;
    }

    /*
     * Asked and answered correctly: one graded answer really happened, so it is
     * recorded as one.
     *
     * No review is scheduled from it, though, and that is deliberate. Spaced
     * review is for what a student has learned here, and a placement answer is
     * not that — scheduling one would have put nine review cards, Counting
     * among them, on the home page of a student placed at Grade 12 two days
     * after they took the test. The estimate stands until practice replaces it.
     */
    concepts[conceptId] = {
      ...previous,
      ...(placed ? { placed: true } : {}),
      attempts: Math.max(previous.attempts, 1),
      correct: Math.max(previous.correct, 1),
      recent: previous.recent.length > 0 ? previous.recent : [true],
      lessonCompleted: previous.lessonCompleted,
      firstStudiedAt: previous.firstStudiedAt ?? input.now,
      lastPracticedAt: previous.lastPracticedAt ?? input.now,
    };
  }
  return {
    ...profile,
    concepts,
    ...(input.summary
      ? // Recording the level the test arrived at is the point of taking it, and
        // it is what stops the recommender offering Grade 12 students the
        // easiest concepts in the graph. It still locks nothing and the student
        // can still change it.
        { diagnostic: input.summary, levelId: input.summary.recommendedLevel }
      : {}),
  };
}

/** Accuracy over the most recent `count` attempts, for the dashboards. */
export function recentAccuracy(profile: LearnerProfile, count = 50): number | undefined {
  const graded = profile.attempts.filter((attempt) => attempt.status !== 'malformed').slice(-count);
  if (graded.length === 0) return undefined;
  return graded.filter((attempt) => attempt.correct).length / graded.length;
}

/** Misconceptions seen most often, worst first — the parent/teacher summary. */
export function topMisconceptions(profile: LearnerProfile, limit = 3): { id: string; count: number }[] {
  const tally = new Map<string, number>();
  for (const state of Object.values(profile.concepts)) {
    for (const [id, count] of Object.entries(state.misconceptions)) {
      tally.set(id, (tally.get(id) ?? 0) + count);
    }
  }
  return [...tally.entries()]
    .map(([id, count]) => ({ id, count }))
    .sort((a, b) => b.count - a.count)
    .slice(0, limit);
}

/** Minutes studied per day over the last `days` days, for the activity chart. */
export function activityByDay(profile: LearnerProfile, now: number, days = 28): { day: string; attempts: number }[] {
  const buckets = new Map<string, number>();
  const dayMs = 86_400_000;
  for (let i = days - 1; i >= 0; i -= 1) {
    const date = new Date(now - i * dayMs);
    const key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
    buckets.set(key, 0);
  }
  for (const attempt of profile.attempts) {
    const date = new Date(attempt.at);
    const key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
    if (buckets.has(key)) buckets.set(key, (buckets.get(key) ?? 0) + 1);
  }
  return [...buckets.entries()].map(([day, attempts]) => ({ day, attempts }));
}
